@file:OptIn(ExperimentalBlockRichTextApi::class)

package com.taocent.simple.compose.component.blockrichtext

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import com.taocent.simple.compose.component.richtext.core.internal.serialization.RichTextSerializer
import com.taocent.simple.compose.component.blockrichtext.internal.registry.BlockRegistry
import com.taocent.simple.compose.component.blockrichtext.internal.serialization.TextBlockSerializer
import com.taocent.simple.compose.component.blockrichtext.internal.serialization.TableBlockSerializer

/**
 * BlockState Block JSON 序列化与粘贴识别相关的扩展函数。
 *
 * - [loadFromJson] 加载 JSON 替换当前所有块
 * - [pasteJson] 粘贴识别:是 Block JSON 则整体替换,否则返回 false
 * - [isBlockJson] 快速判断字符串是否为 Block JSON
 * - [loadBlocksFromJson] 通用 JSON → blocks 解析
 * - [loadTextBlock] / [loadLegacyTextBlock] / [loadTableBlock] 单块解析
 * - [parseBlockJsonArray] 顶层 `{"blocks":[...]}` 数组分割
 */

fun BlockState.loadFromJson(json: String) {
    _blocks.clear()
    rebuildIdToIndex()
    blockStates.clear()
    blockUndoManager.clear()
    loadBlocksFromJson(json)
    setFocusedBlockCompat(_blocks.first().id)
}

fun BlockState.pasteJson(json: String): Boolean {
    return try {
        if (!isBlockJson(json)) return false
        val before = documentSnapshot()
        _blocks.clear()
        blockStates.clear()
        loadBlocksFromJson(json)
        setFocusedBlockCompat(_blocks.first().id)
        clearTableCellFocus()
        selectedTableBlockId = null
        navClearSelection()
        recordDocumentChange(before)
        true
    } catch (_: Exception) {
        false
    }
}

fun BlockState.isBlockJson(json: String): Boolean {
    return parseBlockJsonArray(json).isNotEmpty()
}

private val jsonRegistry: BlockRegistry by lazy {
    BlockRegistry().apply {
        registerSerializer(TextBlockSerializer())
        registerSerializer(TableBlockSerializer())
    }
}

fun BlockState.loadBlocksFromJson(json: String) {
    val blockJsons = parseBlockJsonArray(json)
    if (blockJsons.isEmpty()) {
        val block = TextBlock()
        _blocks.add(block)
        blockStates[block.id] = createBlockState()
    } else {
        for (blockJson in blockJsons) {
            val root = RichTextSerializer.parseJsonObject(blockJson)
            val typeName = root["type"] as? String
            if (typeName != null) {
                val block = jsonRegistry.fromJson(typeName, root)
                if (block != null) {
                    _blocks.add(block)
                    if (block is TextBlock) {
                        val state = createBlockState()
                        state.restoreTextFieldValue(
                            TextFieldValue(
                                annotatedString = block.content,
                                selection = TextRange(block.content.text.length)
                            )
                        )
                        blockStates[block.id] = state
                    }
                    continue
                }
            }
            loadLegacyTextBlock(root)
        }
    }
    rebuildIdToIndex()
}

fun BlockState.loadTextBlock(root: Map<String, Any?>) {
    val content = root["content"] as? Map<*, *> ?: return loadLegacyTextBlock(root)
    val annotated = RichTextSerializer.fromJsonMap(content)
    val block = TextBlock(content = annotated)
    _blocks.add(block)
    val state = createBlockState()
    state.restoreTextFieldValue(
        TextFieldValue(
            annotatedString = annotated,
            selection = TextRange(annotated.text.length)
        )
    )
    blockStates[block.id] = state
}

fun BlockState.loadLegacyTextBlock(root: Map<String, Any?>) {
    val annotated = RichTextSerializer.fromJsonMap(root)
    val block = TextBlock(content = annotated)
    _blocks.add(block)
    val state = createBlockState()
    state.restoreTextFieldValue(
        TextFieldValue(
            annotatedString = annotated,
            selection = TextRange(annotated.text.length)
        )
    )
    blockStates[block.id] = state
}

fun BlockState.loadTableBlock(root: Map<String, Any?>) {
    val rowItems = root["rows"] as? List<*> ?: emptyList<Any?>()
    val rows = rowItems.mapNotNull { rowItem ->
        val rowMap = rowItem as? Map<*, *> ?: return@mapNotNull null
        val cellItems = rowMap["cells"] as? List<*> ?: emptyList<Any?>()
        val cells = cellItems.mapNotNull { cellItem ->
            val cellMap = cellItem as? Map<*, *> ?: return@mapNotNull null
            val contentMap = cellMap["content"] as? Map<*, *>
            val content = if (contentMap != null) {
                RichTextSerializer.fromJsonMap(contentMap)
            } else {
                AnnotatedString("")
            }
            TableBlock.TableCell(content = content)
        }
        if (cells.isEmpty()) null else TableBlock.TableRow(cells = cells)
    }
    val columns = (root["columns"] as? Double)?.toInt() ?: rows.firstOrNull()?.cells?.size ?: 1
    val normalizedRows = if (rows.isEmpty()) {
        listOf(TableBlock.TableRow(cells = List(columns.coerceAtLeast(1)) { TableBlock.TableCell() }))
    } else {
        rows.map { row ->
            val cells = when {
                row.cells.size == columns -> row.cells
                row.cells.size < columns -> row.cells + List(columns - row.cells.size) { TableBlock.TableCell() }
                else -> row.cells.take(columns)
            }
            row.copy(cells = cells)
        }
    }
    _blocks.add(TableBlock(rows = normalizedRows, columnCount = columns.coerceAtLeast(1)))
}

fun BlockState.parseBlockJsonArray(json: String): List<String> {
    val trimmed = json.trim()
    if (!trimmed.startsWith("{\"blocks\":[")) return emptyList()
    val arrayContent = trimmed.removePrefix("{\"blocks\":[").removeSuffix("]}")
    if (arrayContent.isBlank()) return emptyList()
    val results = mutableListOf<String>()
    var depth = 0
    var start = -1
    for (i in arrayContent.indices) {
        when (arrayContent[i]) {
            '{' -> {
                if (depth == 0) start = i
                depth++
            }
            '}' -> {
                depth--
                if (depth == 0 && start >= 0) {
                    results.add(arrayContent.substring(start, i + 1))
                    start = -1
                }
            }
        }
    }
    return results
}
